import React, { Fragment } from 'react'

import { useUnit } from 'effector-react'

import { useCallback, useEffect, useLoading, useRef } from 'hooks'

import { isAuthorizationError } from 'helpers/errors'

import { showFailToast } from 'app/redux/actions/ui'
import { request2faStatus, resendPushFor2fa, type TwoFactorStatusResponse } from 'app/redux/api/2fa'
import { ApiError } from 'app/redux/models/errors'

import AllCenter from 'components/_old/AllCenter/AllCenter.jsx'
import appPush from 'components/_old/TwoFactorAwait/appPush.png'
import { TwoFactorAwaitMainInfo } from 'components/_old/TwoFactorAwait/components/TwoFactorAwaitMainInfo'
import { TwoFactorNotificationIssue } from 'components/_old/TwoFactorAwait/components/TwoFactorNotificationIssue'

import { Paper } from 'components/atoms/Paper'

import { $isIssueShown, goNext, goBack } from './model'

type PushNotificationProps = {
  query: Record<string, string>
  onAccept: () => void
  onDeny: () => void
  onError: () => void
}

const PushNotification = ({
  query,
  onAccept: handleAccept,
  onDeny: handleDeny,
  onError: handleError,
}: PushNotificationProps): React.ReactElement => {
  const { isLoading: isResendingPush, wait: waitForResend } = useLoading(false)

  const [isIssueShown, handleGoNext, handleGoBack] = useUnit([$isIssueShown, goNext, goBack])

  const interval = useRef<NodeJS.Timeout | null>(null)

  const { client_id: clientId, token } = query

  const getPushNotificationStatus = useCallback((): void => {
    request2faStatus({ clientId, token }).then((response) => {
      if (response instanceof ApiError) {
        if (isAuthorizationError(response)) handleDeny()
        else {
          showFailToast('Error while requesting 2FA Status')
          if (typeof handleError === 'function') handleError()
        }
      }
      if ((response as TwoFactorStatusResponse)?.state === 'ALLOWED') {
        handleAccept()
      }
      if ((response as TwoFactorStatusResponse)?.state === 'SMS_REQUIRED') handleAccept()
      if ((response as TwoFactorStatusResponse)?.state === 'DENIED') handleDeny()
      if ((response as TwoFactorStatusResponse)?.state !== 'REQUESTED' && interval.current)
        clearInterval(interval.current)
    })
  }, [clientId, token, handleAccept, handleDeny, handleError])

  useEffect(() => {
    interval.current = setInterval(() => {
      getPushNotificationStatus()
    }, 2000)

    return () => {
      if (interval.current) clearInterval(interval.current)
    }
  }, [getPushNotificationStatus])

  const handleResendPush = async (): Promise<void> => {
    const response = await waitForResend(resendPushFor2fa({ clientId, token }))

    if (response instanceof ApiError) showFailToast('Error while requesting push message resend')
  }

  return (
    <Fragment>
      <AllCenter>
        <Paper bottom={32}>
          <img src={appPush} width={80} height={80} />
        </Paper>
      </AllCenter>
      {isIssueShown ? (
        <TwoFactorNotificationIssue onContinue={handleGoBack} />
      ) : (
        <TwoFactorAwaitMainInfo
          isResendingPush={isResendingPush}
          onResend={handleResendPush}
          onShowNotificationIssue={handleGoNext}
        />
      )}
    </Fragment>
  )
}

export { PushNotification }
